import { Mail, Phone, MapPin, Clock, Send } from "lucide-react";
import { useState } from "react";

const Contact = () => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    subject: "",
    message: "",
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccess("");
    setError("");

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await res.json();

      if (data.success) {
        setSuccess("Thank you! Your message has been sent successfully.");

        setFormData({
          fullName: "",
          email: "",
          subject: "",
          message: "",
        });
      } else {
        setError("Something went wrong. Please try again.");
      }
    } catch (err) {
      console.error(err);
      setError("Server error. Please try again later.");
    } finally {
      setLoading(false);
    }
  };


  return (
    
    <div className="pt-20 bg-gradient-to-b from-gray-50 to-white">
      {/* ================= HERO / BANNER ================= */}
      <section
        className="relative h-[55vh] lg:h-[65vh] flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage:
            "url(https://images.unsplash.com/photo-1581091870627-3f89c4f8a6f0?auto=format&fit=crop&w=1600&q=80)",
        }}
      >
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/70 to-blue-900/70"></div>

        {/* Pattern */}
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              "radial-gradient(circle, white 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        ></div>

        <div className="relative max-w-5xl mx-auto px-4 text-center text-white">
          <span className="inline-block mb-4 px-6 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 text-sm tracking-wide">
            CONTACT WE UNITED CONSTRUCTIONS
          </span>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 leading-tight">
            Let’s Talk About Your{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F57C00] to-orange-400">
              Project
            </span>
          </h1>

          <p className="text-lg lg:text-xl text-gray-200 max-w-2xl mx-auto">
            Get in touch with We United Constructions Ltd to discuss your
            project, request a quotation, or arrange a consultation.
          </p>
        </div>
      </section>

      {/* ================= CONTENT ================= */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-16">
          {/* LEFT: CONTACT INFO */}
          <div className="space-y-12">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-[#082C5A] mb-4">
                Get In Touch
              </h2>
              <div className="w-20 h-1.5 bg-gradient-to-r from-[#F57C00] to-orange-400 rounded-full mb-6"></div>
              <p className="text-gray-600 leading-relaxed max-w-lg">
                We are happy to discuss your requirements and provide
                professional guidance for your residential, commercial, or civil
                construction project.
              </p>
            </div>

            {/* INFO CARDS */}
            <div className="grid sm:grid-cols-2 gap-6">
              <InfoCard
                icon={<Mail />}
                title="Email"
                value={
                  <a
                    href="mailto:Hello@weunited.co.uk"
                    className="hover:text-[#F57C00] transition break-all"
                  >
                    Hello@weunited.co.uk
                  </a>
                }
              />

              <InfoCard
                icon={<Clock />}
                title="Business Hours"
                value="Monday to Friday – 8:30am to 5:30pm"
              />
            </div>
          </div>

          {/* RIGHT: FORM */}
          <div className="relative">
            <div className="absolute -inset-6 bg-gradient-to-br from-blue-200/40 to-orange-200/40 rounded-3xl blur-2xl"></div>

            <div className="relative bg-white rounded-3xl shadow-2xl p-10">
              <h3 className="text-2xl font-bold text-[#082C5A] mb-2">
                Send Us a Message
              </h3>
              <p className="text-gray-600 mb-8">
                Fill out the form below and our team will get back to you
                shortly.
              </p>

              <form className="space-y-6" onSubmit={handleSubmit}>
                {success && (
                  <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
                    {success}
                  </div>
                )}

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                    {error}
                  </div>
                )}
                <Input label="Full Name" name="fullName" placeholder="Enter your name" required  value={formData.fullName} onChange={handleChange} />
                <Input
                  label="Email Address"
                  type="email"
                  placeholder="Enter your email"
                  required
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                />
                <Input label="Subject" placeholder="Subject" required name="subject" value={formData.subject} onChange={handleChange} />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Message
                  </label>
                  <textarea
                    rows="5"
                    placeholder="Write your message"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#0B3C8A]"
                    required
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full bg-gradient-to-r from-[#0B3C8A] to-blue-800 text-white py-4 rounded-lg font-semibold transition flex items-center justify-center gap-2 shadow-xl ${
                    loading ? "opacity-60 cursor-not-allowed" : "hover:opacity-90"
                  }`}
                >
                  {loading ? "Sending..." : "Send Message"}
                  {!loading && <Send size={18} />}
                </button>

              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

/* ================= REUSABLE COMPONENTS ================= */

const InfoCard = ({ icon, title, value }) => (
  <div className="flex items-start gap-4 p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition">
    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center text-[#0B3C8A]">
      {icon}
    </div>
    <div>
      <p className="font-semibold text-gray-800">{title}</p>
      <div className="text-gray-600 text-sm mt-1">{value}</div>
    </div>
  </div>
);

const Input = ({ label, name, type = "text", placeholder , required, value = '', onChange, }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label}
    </label>
    <input
      type={type}
      placeholder={placeholder}
      required={required}
      value={value}
      onChange={onChange}
      name={name}
      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#0B3C8A]"
    />
  </div>
);

export default Contact;
